import { CACHE_CONFIG } from '@/common/constants'
import { StatsCard } from '@/components/StatsCard/StatsCard'
import { useDealership } from '@/contexts/DealershipContext'
import { getGroupDashboardStats } from '@/services/dealership.api'
import { Dealership, DealershipGroup } from '@/types'
import {
  formatGroupStatsRangeLabel,
  getIntervalIsoRange,
  type GroupInterval,
} from '@/utils/groupDashboardInterval'
import { useQuery } from '@tanstack/react-query'
import { Card, Grid, Group, SegmentedControl, SimpleGrid, Stack, Text, Title } from '@mantine/core'
import {
  IconCalendarEvent,
  IconMessageCircle,
  IconRobot,
  IconSettings,
  IconUserX,
  IconUsers,
  IconWalk,
} from '@tabler/icons-react'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

export type { GroupInterval }

export function GroupDashboard() {
  const {
    selectedEntity,
    setSelectedEntity,
    findEntityById,
    groupDashboardInterval,
    setGroupDashboardInterval,
  } = useDealership()
  const navigate = useNavigate()

  const groupId = selectedEntity?.type === 'group' ? selectedEntity.id : ''

  const { startDate, endDate } = useMemo(() => getIntervalIsoRange(groupDashboardInterval), [groupDashboardInterval])

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['group-stats', groupId, groupDashboardInterval],
    queryFn: () => getGroupDashboardStats(groupId, startDate, endDate),
    staleTime: CACHE_CONFIG.STALE_TIME.STATS,
    gcTime: CACHE_CONFIG.GC_TIME.DEFAULT,
    retry: CACHE_CONFIG.RETRY_COUNT,
    refetchOnWindowFocus: false,
    enabled: !!groupId,
  })

  if (!selectedEntity || selectedEntity.type !== 'group') {
    return <Text>Please select a dealership group</Text>
  }

  /** Prefer live tree from context so dealership rows match merged stats */
  const group =
    (findEntityById(selectedEntity.id, 'group') as DealershipGroup | null) ?? (selectedEntity as DealershipGroup)

  const rangeLabel = stats?.dateRange
    ? formatGroupStatsRangeLabel(stats.dateRange.startDate, stats.dateRange.endDate)
    : formatGroupStatsRangeLabel(startDate, endDate)

  const handleViewDealership = (dealershipId: string) => {
    const dealer = findEntityById(dealershipId, 'dealership')
    if (dealer) {
      setSelectedEntity(dealer)
      navigate('/dealership/configuration')
    }
  }

  const mergeDealershipRow = (d: Dealership) => {
    const row = stats?.dealerships?.find(ds => ds.dealershipId === d.id)
    return {
      totalConversations: row?.totalConversations ?? d.totalConversations ?? 0,
      totalAppointments: row?.totalAppointments ?? d.totalAppointments ?? 0,
      upcomingAppointments: row?.upcomingAppointments ?? d.upcomingAppointments ?? 0,
      conversationsInternetNew: row?.conversationsInternetNew ?? 0,
      conversationsFirstContact: row?.conversationsFirstContact ?? 0,
      totalAppointmentsAi: row?.totalAppointmentsAi ?? 0,
    }
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start" wrap="wrap">
        <div>
          <Title order={2}>{group.name} Overview</Title>
          <Text size="sm" c="dimmed">
            Monitor performance across all dealerships in the group
          </Text>
          <Text size="xs" c="dimmed" mt={2}>
            Metrics for {rangeLabel}
            {statsLoading ? ' (loading…)' : ''}
          </Text>
        </div>
        <SegmentedControl
          value={groupDashboardInterval}
          onChange={v => setGroupDashboardInterval(v as GroupInterval)}
          data={[
            { label: 'This Week', value: 'this_week' },
            { label: 'This Month', value: 'this_month' },
            { label: 'Last Month', value: 'last_month' },
            { label: 'All Time', value: 'all_time' },
          ]}
        />
      </Group>

      <Stack gap="xs">
        <Text size="sm" fw={600} c="dimmed" tt="uppercase">
          Engagement
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
          <StatsCard
            title="Leads"
            icon={IconUsers}
            color="violet"
            metricKey="totalLeads"
            dualMetric={
              stats
                ? { total: stats.totalLeads, ai: stats.conversationsFirstContact }
                : { total: 0, ai: 0 }
            }
          />
          <StatsCard
            title="Total Conversations"
            value={stats?.totalConversations ?? 0}
            icon={IconMessageCircle}
            color="cyan"
            metricKey="totalConversations"
          />
          <StatsCard
            title="Active Conversations"
            value={stats?.activeConversations ?? 0}
            icon={IconMessageCircle}
            color="teal"
            metricKey="activeConversations"
          />
        </SimpleGrid>
      </Stack>

      <Stack gap="xs">
        <Text size="sm" fw={600} c="dimmed" tt="uppercase">
          Appointments
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
          <StatsCard
            title="Appointments"
            icon={IconCalendarEvent}
            color="indigo"
            dualMetric={
              stats
                ? { total: stats.totalAppointments, ai: stats.totalAppointmentsAi }
                : { total: 0, ai: 0 }
            }
          />
          <StatsCard
            title="Showroom Visits"
            value={stats?.showroomVisits ?? 0}
            icon={IconWalk}
            color="orange"
          />
          <StatsCard title="Missed" value={stats?.missedAppointments ?? 0} icon={IconUserX} color="red" />
        </SimpleGrid>
      </Stack>

      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="md">
          <Title order={3}>Dealerships</Title>
          <Grid>
            {group.dealerships.map(dealership => {
              const {
                totalConversations,
                totalAppointments,
                upcomingAppointments,
                conversationsInternetNew,
                conversationsFirstContact,
                totalAppointmentsAi,
              } = mergeDealershipRow(dealership)
              return (
                <Grid.Col key={dealership.id} span={{ base: 12, md: 6, lg: 4 }}>
                  <Card
                    withBorder
                    radius="md"
                    p="md"
                    style={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                    onClick={() => handleViewDealership(dealership.id)}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-2px)'
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.12)'
                    }}
                  >
                    <Stack gap="xs">
                      <Group justify="space-between">
                        <Text fw={600} size="lg">
                          {dealership.name}
                        </Text>
                        <Group gap="xs">
                          <Text
                            size="xs"
                            c={dealership.status === 'active' ? 'green' : 'gray'}
                            fw={500}
                          >
                            {dealership.status}
                          </Text>
                          <IconSettings size={14} style={{ opacity: 0.6 }} />
                        </Group>
                      </Group>
                      <Text size="sm" c="dimmed">
                        {dealership.location}
                      </Text>
                      <Grid mt="xs">
                        <Grid.Col span={4}>
                          <Text size="xs" c="dimmed">
                            Leads
                          </Text>
                          <Group gap={6} wrap="nowrap" align="center">
                            <Text size="lg" fw={600}>
                              {totalConversations}
                            </Text>
                            <Text size="sm" c="dimmed">
                              |
                            </Text>
                            <IconRobot size={16} color="var(--mantine-color-violet-6)" aria-hidden />
                            <Text size="lg" fw={600} c="violet">
                              {conversationsFirstContact}
                            </Text>
                          </Group>
                          <Text size="xs" c="dimmed" mt={2}>
                            Int+New: {conversationsInternetNew}
                          </Text>
                        </Grid.Col>
                        <Grid.Col span={4}>
                          <Text size="xs" c="dimmed">
                            Appts
                          </Text>
                          <Group gap={6} wrap="nowrap" align="center">
                            <Text size="lg" fw={600}>
                              {totalAppointments}
                            </Text>
                            <Text size="sm" c="dimmed">
                              |
                            </Text>
                            <IconRobot size={16} color="var(--mantine-color-violet-6)" aria-hidden />
                            <Text size="lg" fw={600} c="violet">
                              {totalAppointmentsAi}
                            </Text>
                          </Group>
                        </Grid.Col>
                        <Grid.Col span={4}>
                          <Text size="xs" c="dimmed">
                            Upcoming
                          </Text>
                          <Text size="lg" fw={600}>
                            {upcomingAppointments}
                          </Text>
                        </Grid.Col>
                      </Grid>
                    </Stack>
                  </Card>
                </Grid.Col>
              )
            })}
          </Grid>
        </Stack>
      </Card>
    </Stack>
  )
}
